import { DiagramCanvas } from "@/features/diagram/DiagramCanvas";
import type { SelectorConfig } from "@/features/diagram/DiagramToolbar";
import {
  useContextDiagram,
  useDataPipelineDiagram,
  useEventFlowDiagram,
  usePluginMap,
  useSystemDiagram,
  useWorkflowDiagram,
} from "@/features/diagram/hooks/useSystemDiagram";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/admin/diagram")({
  component: AdminDiagramPage,
});

type View = "system" | "context" | "workflow" | "plugins" | "events" | "data";

const VIEWS: { value: View; label: string }[] = [
  { value: "system", label: "Обзор системы" },
  { value: "context", label: "Контекст" },
  { value: "workflow", label: "Процессы" },
  { value: "plugins", label: "Плагины" },
  { value: "events", label: "Потоки событий" },
  { value: "data", label: "Данные" },
];

const CONTEXTS = [
  { value: "accounting", label: "Бухгалтерия" },
  { value: "catalog", label: "Каталог" },
  { value: "commerce", label: "Коммерция" },
  { value: "inventory", label: "Склад" },
  { value: "manufacturing", label: "Производство" },
  { value: "iam", label: "Пользователи" },
  { value: "agent", label: "Агент" },
  { value: "communication", label: "Коммуникации" },
  { value: "notification", label: "Уведомления" },
  { value: "automation", label: "Автоматизация" },
  { value: "eval", label: "Eval" },
  { value: "workitem", label: "Задачи" },
];

const WORKFLOWS = [
  { value: "commerce.checkout", label: "Оформление заказа" },
  { value: "commerce.fulfillment", label: "Фулфилмент" },
  { value: "commerce.quote_conversion", label: "Конвертация КП" },
  { value: "order_lifecycle", label: "Жизненный цикл заказа" },
  { value: "accounting.post_journal_entry", label: "Бухгалтерская проводка" },
  { value: "accounting.record_payment", label: "Учёт оплаты" },
  { value: "accounting.reverse_entry", label: "Сторнирование" },
  { value: "dynamic_pipeline", label: "Динамический пайплайн" },
  { value: "yandex_feed_import", label: "Импорт Яндекс.Фида" },
  { value: "eval_runner", label: "Запуск Eval" },
  { value: "enrichment_single", label: "Обогащение товара" },
];

const PLUGIN_DOMAINS = [
  { value: "all", label: "Все домены" },
  { value: "payment", label: "Оплата" },
  { value: "delivery", label: "Доставка" },
  { value: "enrichment", label: "Обогащение" },
  { value: "mrp", label: "Планирование" },
  { value: "accounting", label: "Бухгалтерия" },
  { value: "pipeline", label: "Пайплайны" },
  { value: "event", label: "События" },
  { value: "tax", label: "Налоги" },
];

function AdminDiagramPage() {
  const [view, setView] = useState<View>("system");
  const [selectedContext, setSelectedContext] = useState("commerce");
  const [selectedWorkflow, setSelectedWorkflow] = useState("commerce.checkout");
  const [pluginDomain, setPluginDomain] = useState("all");

  const selectors: SelectorConfig[] = [
    { value: view, options: VIEWS, onChange: (v) => setView(v as View) },
    ...(view === "context" ? [{ value: selectedContext, options: CONTEXTS, onChange: setSelectedContext }] : []),
    ...(view === "workflow" ? [{ value: selectedWorkflow, options: WORKFLOWS, onChange: setSelectedWorkflow }] : []),
    ...(view === "plugins" ? [{ value: pluginDomain, options: PLUGIN_DOMAINS, onChange: setPluginDomain }] : []),
  ];

  return (
    <div className="-mx-6 -mt-4 -mb-6" style={{ height: "calc(100vh - 5.5rem)" }}>
      {view === "system" && <SystemView selectors={selectors} />}
      {view === "context" && <ContextView context={selectedContext} selectors={selectors} />}
      {view === "workflow" && <WorkflowView name={selectedWorkflow} selectors={selectors} />}
      {view === "plugins" && <PluginsView domain={pluginDomain === "all" ? "" : pluginDomain} selectors={selectors} />}
      {view === "events" && <EventFlowView selectors={selectors} />}
      {view === "data" && <DataPipelineView selectors={selectors} />}
    </div>
  );
}

function SystemView({ selectors }: { selectors: SelectorConfig[] }) {
  const { data, isLoading } = useSystemDiagram({ includeWorkflows: false, includePlugins: true, includeEventFlows: true, includeServices: true });
  return <DiagramCanvas graph={data} isLoading={isLoading} selectors={selectors} />;
}

function ContextView({ context, selectors }: { context: string; selectors: SelectorConfig[] }) {
  const { data, isLoading } = useContextDiagram(context);
  return <DiagramCanvas graph={data} isLoading={isLoading} selectors={selectors} />;
}

function WorkflowView({ name, selectors }: { name: string; selectors: SelectorConfig[] }) {
  const { data, isLoading } = useWorkflowDiagram(name);
  return <DiagramCanvas graph={data} isLoading={isLoading} selectors={selectors} />;
}

function PluginsView({ domain, selectors }: { domain: string; selectors: SelectorConfig[] }) {
  const { data, isLoading } = usePluginMap(domain || undefined);
  return <DiagramCanvas graph={data} isLoading={isLoading} selectors={selectors} />;
}

function EventFlowView({ selectors }: { selectors: SelectorConfig[] }) {
  const { data, isLoading } = useEventFlowDiagram();
  return <DiagramCanvas graph={data} isLoading={isLoading} selectors={selectors} />;
}

function DataPipelineView({ selectors }: { selectors: SelectorConfig[] }) {
  const { data, isLoading } = useDataPipelineDiagram();
  return <DiagramCanvas graph={data} isLoading={isLoading} selectors={selectors} />;
}
